/**
 * Окно подтверждения перед письмом.
 *
 * Общее для уведомлений врачу и для счёта клинике: письмо ни при каких
 * обстоятельствах не уходит само, и человек должен увидеть, что именно уйдёт
 * и кому. Забрать письмо обратно нельзя.
 *
 * Текст правится прямо здесь. Шаблон общий на всю лабораторию, а письмо уходит
 * конкретному адресату — добавить строку про этот случай должно быть можно,
 * не переписывая шаблон для всех остальных.
 */

import { useState, type ReactNode } from 'react';
import { Mail, Paperclip, Send, X } from 'lucide-react';
import type { ComposedLetter } from '@otc/shared';
import { Button } from './ui.js';
import { useCloseOnBack } from './useCloseOnBack.js';

export function MailComposeDialog({
  title,
  recipientName,
  toEmail,
  letter,
  attachments,
  note,
  onSend,
  onCancel,
}: {
  title: string;
  recipientName: string;
  toEmail: string;
  letter: ComposedLetter;
  /** Имена вложений — если письмо уходит с файлами. */
  attachments?: string[];
  /** Пояснение под полем текста. */
  note?: ReactNode;
  /** Отправка. Исключение показывается в окне и не закрывает его. */
  onSend: (letter: ComposedLetter) => Promise<void>;
  onCancel: () => void;
}) {
  // «Назад» на телефоне закрывает окно, а не выходит из программы.
  useCloseOnBack(true, onCancel);

  const [subject, setSubject] = useState(letter.subject);
  const [body, setBody] = useState(letter.body);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      await onSend({ subject, body });
    } catch (e) {
      // Окно НЕ закрывается: набранный текст пропал бы вместе с ним, а
      // причина отказа чаще всего поправима прямо здесь.
      setError(e instanceof Error ? e.message : 'Не удалось отправить');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: 'rgba(28,27,24,0.45)' }}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto rounded-xl border p-4"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <span className="flex items-center gap-2">
            <Mail size={18} style={{ color: 'var(--otc-accent)' }} />
            <span className="text-sm font-medium text-ink">{title}</span>
          </span>
          <button onClick={onCancel} className="otc-btn otc-btn-ghost otc-btn-icon" aria-label="Закрыть">
            <X size={16} className="text-ink2" />
          </button>
        </div>

        {/* Адрес показывается, но не правится: его берёт сервер из карточки. */}
        <p className="mb-3 text-xs text-ink2">
          Кому: <span className="text-ink">{recipientName}</span> · <span className="tabular">{toEmail}</span>
        </p>

        <label className="mb-1 block text-xs text-ink2">Тема</label>
        <input className="otc-input mb-3" value={subject} onChange={(e) => setSubject(e.target.value)} />

        <label className="mb-1 block text-xs text-ink2">Текст письма</label>
        <textarea
          className="otc-input mb-1 resize-none"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {note && <p className="mb-3 text-[11px] text-ink2">{note}</p>}

        {attachments && attachments.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {attachments.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-ink2"
                style={{ borderColor: 'var(--otc-line)' }}
              >
                <Paperclip size={11} /> {name}
              </span>
            ))}
          </div>
        )}

        {error && (
          <p
            className="mb-3 rounded-lg px-3 py-2 text-xs"
            style={{ background: 'color-mix(in srgb, var(--otc-coral) 12%, transparent)', color: 'var(--otc-coral)' }}
          >
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => void send()} disabled={sending || !subject.trim() || !body.trim()}>
            <span className="inline-flex items-center gap-1.5">
              <Send size={14} /> {sending ? 'Отправка…' : 'Отправить'}
            </span>
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={sending}>
            Не отправлять
          </Button>
        </div>
      </div>
    </div>
  );
}
